import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search, Users, GraduationCap, TrendingUp,
  Mail, Building, Calendar, BookOpen, Target, Briefcase, Linkedin, ExternalLink,
} from 'lucide-react';
import { useMentorCohorts, useMentorStudents, type MentorStudent } from '@/hooks/useMentorStudents';

export default function StudentsPage() {
  const [search, setSearch] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [selectedCohort, setSelectedCohort] = useState<string>('all');

  const { data: cohorts = [], isLoading: cohortsLoading } = useMentorCohorts();

  // Derive unique organizations from cohorts
  const organizations = useMemo(() => {
    const map = new Map<string, string>();
    cohorts.forEach(c => {
      if (c.organization_id && c.organization_name) map.set(c.organization_id, c.organization_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [cohorts]);

  // Available cohorts based on org filter
  const availableCohorts = useMemo(() => {
    if (selectedOrg === 'all') return cohorts;
    return cohorts.filter(c => c.organization_id === selectedOrg);
  }, [cohorts, selectedOrg]);

  // Cohort IDs to query
  const cohortIdsToQuery = useMemo(() => {
    if (selectedCohort !== 'all') return [selectedCohort];
    return availableCohorts.map(c => c.id);
  }, [availableCohorts, selectedCohort]);

  const { data: students = [], isLoading: studentsLoading } = useMentorStudents(cohortIdsToQuery);

  const handleOrgChange = (value: string) => {
    setSelectedOrg(value);
    setSelectedCohort('all');
  };

  // Filter students
  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      const matchesSearch = s.full_name.toLowerCase().includes(search.toLowerCase()) ||
        (s.company ?? '').toLowerCase().includes(search.toLowerCase()) ||
        s.email.toLowerCase().includes(search.toLowerCase());
      const matchesTab = activeTab === 'all' || s.status === activeTab;
      return matchesSearch && matchesTab;
    });
  }, [students, search, activeTab]);

  const selectedStudent = useMemo(
    () => students.find(s => s.id === selectedStudentId) ?? null,
    [students, selectedStudentId]
  );

  // Stats
  const stats = useMemo(() => {
    const total = filteredStudents.length;
    const active = filteredStudents.filter(s => s.status === 'active').length;
    const atRisk = filteredStudents.filter(s => s.status === 'at-risk').length;
    const avgProgress = total > 0
      ? Math.round(filteredStudents.reduce((acc, s) => acc + s.content_progress, 0) / total)
      : 0;
    return { total, active, atRisk, avgProgress };
  }, [filteredStudents]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Activo</Badge>;
      case 'at-risk':
        return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">En Riesgo</Badge>;
      case 'inactive':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Inactivo</Badge>;
      default:
        return null;
    }
  };

  const isLoading = cohortsLoading || studentsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-primary" />
          Estudiantes
        </h1>
        <p className="text-muted-foreground">Gestiona y da seguimiento a tus estudiantes</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-2 block">Empresa</Label>
              <SearchableCombobox
                value={selectedOrg}
                onSelect={handleOrgChange}
                placeholder="Todas las empresas"
                searchPlaceholder="Buscar empresa..."
                triggerIcon={<Building className="w-4 h-4" />}
                options={[
                  { value: 'all', label: 'Todas las empresas', iconNode: <Building className="w-4 h-4 opacity-70" />, keywords: ['todas','todos'] },
                  ...organizations.map(org => ({
                    value: org.id,
                    label: org.name,
                    iconNode: <Building className="w-4 h-4 opacity-70" />,
                  })),
                ]}
              />
            </div>
            <div className="flex-1">
              <Label className="text-sm font-medium mb-2 block">Cohorte</Label>
              <SearchableCombobox
                value={selectedCohort}
                onSelect={setSelectedCohort}
                placeholder="Todas las cohortes"
                searchPlaceholder="Buscar cohorte..."
                triggerIcon={<Calendar className="w-4 h-4" />}
                options={[
                  { value: 'all', label: 'Todas las cohortes', iconNode: <Calendar className="w-4 h-4 opacity-70" />, keywords: ['todas','todos'] },
                  ...availableCohorts.map(cohort => ({
                    value: cohort.id,
                    label: cohort.name,
                    iconNode: <Calendar className="w-4 h-4 opacity-70" />,
                  })),
                ]}
              />
            </div>
            {(selectedOrg !== 'all' || selectedCohort !== 'all') && (
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => { setSelectedOrg('all'); setSelectedCohort('all'); }}>
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{isLoading ? '–' : stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Estudiantes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{isLoading ? '–' : stats.active}</p>
                <p className="text-xs text-muted-foreground">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{isLoading ? '–' : stats.atRisk}</p>
                <p className="text-xs text-muted-foreground">En Riesgo</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{isLoading ? '–' : `${stats.avgProgress}%`}</p>
                <p className="text-xs text-muted-foreground">Progreso Promedio</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Students List */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-lg">Lista de Estudiantes</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar estudiante..."
                    className="pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="all">Todos</TabsTrigger>
                  <TabsTrigger value="active">Activos</TabsTrigger>
                  <TabsTrigger value="at-risk">En Riesgo</TabsTrigger>
                  <TabsTrigger value="inactive">Inactivos</TabsTrigger>
                </TabsList>
                <TabsContent value={activeTab} className="space-y-3">
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center gap-3">
                          <Skeleton className="w-10 h-10 rounded-full" />
                          <div className="space-y-1">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                        <Skeleton className="h-2 w-full" />
                      </div>
                    ))
                  ) : filteredStudents.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p>No se encontraron estudiantes</p>
                    </div>
                  ) : (
                    filteredStudents.map(student => (
                      <div
                        key={`${student.id}-${student.cohort_id}`}
                        onClick={() => setSelectedStudentId(student.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          selectedStudentId === student.id ? 'border-primary bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={student.avatar_url ?? undefined} />
                              <AvatarFallback>{student.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{student.full_name}</p>
                              <p className="text-sm text-muted-foreground">{student.organization_name ?? student.company ?? '—'}</p>
                            </div>
                          </div>
                          {getStatusBadge(student.status)}
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Contenido</span>
                            <span className="font-medium">{student.content_progress}%</span>
                          </div>
                          <Progress value={student.content_progress} className="h-2" />
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{student.cohort_name}</span>
                          <span>{student.deliverables_count} entregable{student.deliverables_count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Student Detail */}
        <div className="space-y-4">
          {selectedStudent ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Perfil del Estudiante</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={selectedStudent.avatar_url ?? undefined} />
                    <AvatarFallback className="text-lg">{selectedStudent.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-lg">{selectedStudent.full_name}</p>
                    {getStatusBadge(selectedStudent.status)}
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedStudent.email}</span>
                  </div>
                  {selectedStudent.position && (
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedStudent.position}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedStudent.organization_name ?? selectedStudent.company ?? '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedStudent.cohort_name}</span>
                  </div>
                  {selectedStudent.enrolled_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>Inscrito: {new Date(selectedStudent.enrolled_at).toLocaleDateString('es-MX')}</span>
                    </div>
                  )}
                  {selectedStudent.linkedin_url && (
                    <a
                      href={selectedStudent.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>

                {selectedStudent.bio && (
                  <div className="pt-2">
                    <p className="text-sm font-medium mb-1">Bio</p>
                    <p className="text-sm text-muted-foreground">{selectedStudent.bio}</p>
                  </div>
                )}

                <div className="pt-2">
                  <p className="text-sm font-medium mb-2">Progreso Académico</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avance en Contenido</span>
                      <span className="font-medium">{selectedStudent.content_progress}%</span>
                    </div>
                    <Progress value={selectedStudent.content_progress} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {selectedStudent.deliverables_count} entregable{selectedStudent.deliverables_count !== 1 ? 's' : ''} enviado{selectedStudent.deliverables_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {selectedStudent.project_name && (
                  <div className="pt-2">
                    <p className="text-sm font-medium mb-1">Proyecto Actual</p>
                    <div className="p-3 border rounded-lg">
                      <p className="font-medium text-sm">{selectedStudent.project_name}</p>
                      {selectedStudent.project_status && (
                        <Badge variant="outline" className="mt-1 text-xs capitalize">
                          {selectedStudent.project_status.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {selectedStudent.last_login && (
                  <p className="text-xs text-muted-foreground pt-2">
                    Último acceso: {new Date(selectedStudent.last_login).toLocaleDateString('es-MX')}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Selecciona un estudiante para ver su perfil</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
